/**
 * Slim discovery strip — iter 67dx.
 *
 * Sits right under the hero search on `/` and surfaces the same top-10
 * destinations that the full carousel below renders as cards. Renters
 * never have to scroll to find the SEO landing pages — one tap on
 * "Tirana Airport" pill jumps straight to `/car-rental/tirana-airport-…`.
 *
 * The chip row re-uses `/api/locations/landing-index` (same payload as
 * the big TopLocationDestinations carousel below) — keeps cache hits
 * efficient and the order in sync.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Plane, Waves, MapPin, ChevronRight } from "lucide-react";

const FEATURED_AIRPORTS = ["Tirana Airport", "Vlora Airport", "Pristina Airport"];
const FEATURED_HOLIDAYS = ["Sarande", "Durres", "Ksamil", "Vlore"];
const FEATURED_OTHER    = ["Tirana", "Pristina", "Skopje"];

function _normalize(s) {
  return (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function _kindIcon(kind) {
  if (kind === "airport") return Plane;
  if (kind === "port" || kind === "tourist") return Waves;
  return MapPin;
}

export default function TopDestinationsChipRow() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/locations/landing-index")
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  const chips = useMemo(() => {
    if (!rows.length) return [];
    const lookup = new Map();
    for (const r of rows) {
      for (const key of [r.name, ...(r.aliases || [])]) {
        const n = _normalize(key);
        if (n && !lookup.has(n)) lookup.set(n, r);
      }
    }
    const out = [];
    const picked = new Set();
    [...FEATURED_AIRPORTS, ...FEATURED_HOLIDAYS, ...FEATURED_OTHER].forEach((name) => {
      const hit = lookup.get(_normalize(name));
      if (hit && !picked.has(hit.slug)) { picked.add(hit.slug); out.push(hit); }
    });
    return out.slice(0, 10);
  }, [rows]);

  if (chips.length === 0) return null;

  return (
    <div
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2 -mt-2"
      data-testid="top-destinations-chip-row"
    >
      <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
        <ChevronRight className="h-3 w-3" />
        Top destinations
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map((loc) => {
          const Icon = _kindIcon(loc.kind);
          return (
            <Link
              key={loc.slug}
              to={`/car-rental/${loc.slug}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-amber-200 text-neutral-800 text-xs font-semibold hover:border-neutral-900 hover:bg-neutral-900 hover:text-amber-400 transition shadow-sm"
              data-testid={`top-destinations-chip-${loc.slug}`}
            >
              <Icon className="h-3 w-3 text-amber-600 group-hover:text-amber-400" />
              {loc.name}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
